// Modale d'ajout ET de modification d'un routeur MikroTik.
//
// Aucun agent n'est installé sur un routeur : c'est la PLATEFORME qui s'y
// connecte en SSH. D'où le bouton « Tester la connexion », qui montre au
// technicien ce que le serveur voit (identité, modèle, version) et surtout
// l'empreinte de la clé SSH : elle est mémorisée à l'ajout, et une clé
// différente par la suite bloque l'accès (protection contre un homme du milieu).
//
// `device` nul = ajout ; sinon modification (mot de passe vide = inchangé).

import { useEffect, useState } from 'react';
import { X, Router, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { ClientPicker } from './ClientPicker.jsx';
import { mikrotikCall, testFailureMessage } from '../lib/mikrotik.jsx';

const labelCls = 'block text-[11px] text-muted-2 mb-1';
const inputCls =
  'w-full bg-sidebar border border-rule rounded-lg px-3 py-2 text-fg-2 text-xs outline-none focus:border-blue-500';

export function MikrotikDeviceModal({ device, onClose }) {
  const editing = !!device;
  const [form, setForm] = useState(() => ({
    clientId: device?.clientId || '',
    siteId: device?.siteId || '',
    host: device?.host || '',
    sshPort: device?.sshPort || 22,
    winboxPort: device?.winboxPort || 8291,
    username: device?.username || '',
    password: '',
    label: device?.label || '',
    backupEnabled: device ? !!device.backupEnabled : true,
    jumpHostId: device?.jumpHostId || '',
  }));
  // Serveurs de rebond disponibles ; à la création, le rebond par défaut est présélectionné.
  const [jumps, setJumps] = useState([]);
  const [sites, setSites] = useState([]);
  const [testing, setTesting] = useState(false);
  // Résultat du dernier test ; invalidé dès qu'un champ d'accès change, sinon
  // l'empreinte affichée pourrait ne plus correspondre à ce qui sera envoyé.
  const [test, setTest] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get('/platform/mikrotik/jumphosts')
      .then((list) => {
        if (!active) return;
        setJumps(list || []);
        const def = (list || []).find((j) => j.isDefault);
        if (!device && def) setForm((f) => (f.jumpHostId ? f : { ...f, jumpHostId: def.id }));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!form.clientId) {
      setSites([]);
      return;
    }
    let active = true;
    api.get('/sites?clientId=' + form.clientId).then((s) => {
      if (active) setSites(s || []);
    });
    return () => {
      active = false;
    };
  }, [form.clientId]);

  const setField = (patch, resetsTest = false) => {
    setForm((f) => ({ ...f, ...patch }));
    if (resetsTest) setTest(null);
    setError(null);
  };

  const hostOk = form.host.trim().length > 0;
  const portsOk = [form.sshPort, form.winboxPort].every((p) => Number(p) >= 1 && Number(p) <= 65535);
  // En modification, un mot de passe vide veut dire « garder l'actuel ».
  const accessOk = hostOk && form.username.trim().length > 0 && (editing || form.password.length > 0);
  const canSubmit = accessOk && portsOk && !!form.siteId && !saving;

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    setError(null);
    const { ok, status, json } = await mikrotikCall('/platform/mikrotik/test', 'POST', {
      host: form.host.trim(),
      sshPort: Number(form.sshPort),
      username: form.username.trim(),
      password: form.password || null,
      agentId: editing ? device.agentId : null,
      jumpHostId: form.jumpHostId || null,
    });
    setTesting(false);
    if (ok) setTest(json);
    else setError(json?.error || 'Échec du test de connexion (erreur ' + status + ').');
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    let r;
    if (editing) {
      // Champs inchangés laissés à null : le serveur ne touche pas ce qui est null.
      const body = {
        siteId: form.siteId !== device.siteId ? form.siteId : null,
        label: form.label.trim() !== (device.label || '') ? form.label.trim() : null,
        host: form.host.trim() !== device.host ? form.host.trim() : null,
        sshPort: Number(form.sshPort) !== device.sshPort ? Number(form.sshPort) : null,
        winboxPort: Number(form.winboxPort) !== device.winboxPort ? Number(form.winboxPort) : null,
        username: form.username.trim() !== device.username ? form.username.trim() : null,
        password: form.password || null,
        backupEnabled: form.backupEnabled !== !!device.backupEnabled ? form.backupEnabled : null,
        // "" = repasser en direct ; null = inchangé.
        jumpHostId: form.jumpHostId !== (device.jumpHostId || '') ? form.jumpHostId : null,
      };
      r = await mikrotikCall('/platform/mikrotik/' + device.agentId, 'PUT', body);
    } else {
      r = await mikrotikCall('/platform/mikrotik', 'POST', {
        siteId: form.siteId,
        label: form.label.trim() || null,
        host: form.host.trim(),
        sshPort: Number(form.sshPort),
        winboxPort: Number(form.winboxPort),
        username: form.username.trim(),
        password: form.password,
        // Empreinte vue au test : épinglée, et l'ajout échoue si l'hôte en
        // présente une autre entre-temps.
        expectedFingerprint: test?.success ? test.hostKeyFingerprint : null,
        backupEnabled: form.backupEnabled,
        jumpHostId: form.jumpHostId || null,
      });
    }
    setSaving(false);
    if (r.ok) {
      onClose(true, r.json);
      return;
    }
    if (r.status === 422 || r.status === 409) {
      // Le serveur rend le résultat du test : on l'affiche comme un test manuel.
      setTest(r.json && r.json.status ? r.json : null);
      setError(
        r.status === 409
          ? "Le routeur présente maintenant une autre clé SSH que celle vue au test : rien n'a été enregistré. Relancer le test pour vérifier l'empreinte."
          : testFailureMessage(r.json)
      );
    } else {
      setError(r.json?.error || "Échec de l'enregistrement (erreur " + r.status + ').');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
      <div className="bg-card border border-rule rounded-2xl w-[560px] max-w-full max-h-[92vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 pt-6 pb-3 flex-none flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Router size={18} strokeWidth={1.75} className="text-blue-400" />
            <h2 className="text-base font-bold text-fg">
              {editing ? "Modifier l'accès au routeur" : 'Ajouter un routeur MikroTik'}
            </h2>
          </div>
          <button
            onClick={() => onClose(false)}
            className="bg-transparent border-none text-muted cursor-pointer hover:text-fg p-1"
            aria-label="Fermer"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="px-6 pb-6 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Client</label>
              <ClientPicker
                value={form.clientId || null}
                onChange={(id) => setField({ clientId: id || '', siteId: '' })}
                placeholder="Sélectionner un client…"
              />
            </div>
            <div>
              <label className={labelCls}>Site</label>
              <select
                className={`${inputCls} cursor-pointer`}
                value={form.siteId}
                disabled={!form.clientId}
                onChange={(e) => setField({ siteId: e.target.value })}
              >
                <option value="">-- Sélectionner --</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className={labelCls}>Connexion</label>
              <select
                className={`${inputCls} cursor-pointer`}
                value={form.jumpHostId}
                onChange={(e) => setField({ jumpHostId: e.target.value }, true)}
              >
                <option value="">Directe</option>
                {jumps.map((j) => (
                  <option key={j.id} value={j.id}>
                    Via {j.name} ({j.host})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelCls}>Hôte / adresse IP</label>
              <input
                className={inputCls}
                value={form.host}
                placeholder="192.168.88.1"
                onChange={(e) => setField({ host: e.target.value }, true)}
              />
              {form.jumpHostId && (
                <div className="text-[10px] text-muted-2 mt-1">
                  Adresse du routeur telle que le serveur de rebond la voit
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Port SSH</label>
                <input
                  type="number"
                  min="1"
                  max="65535"
                  className={inputCls}
                  value={form.sshPort}
                  onChange={(e) => setField({ sshPort: e.target.value }, true)}
                />
              </div>
              <div>
                <label className={labelCls}>Port WinBox</label>
                <input
                  type="number"
                  min="1"
                  max="65535"
                  className={inputCls}
                  value={form.winboxPort}
                  onChange={(e) => setField({ winboxPort: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>Utilisateur RouterOS</label>
              <input
                className={inputCls}
                value={form.username}
                autoComplete="off"
                onChange={(e) => setField({ username: e.target.value }, true)}
              />
            </div>
            <div>
              <label className={labelCls}>
                Mot de passe{editing ? ' (vide = inchangé)' : ''}
              </label>
              <input
                type="password"
                className={inputCls}
                value={form.password}
                autoComplete="new-password"
                placeholder={editing && device.hasPassword ? '••••••••' : ''}
                onChange={(e) => setField({ password: e.target.value }, true)}
              />
            </div>

            <div className="col-span-2">
              <label className={labelCls}>Nom affiché (facultatif)</label>
              <input
                className={inputCls}
                value={form.label}
                placeholder="Par défaut : l'identité du routeur"
                onChange={(e) => setField({ label: e.target.value })}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 mt-3 text-[12px] text-fg-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.backupEnabled}
              onChange={(e) => setField({ backupEnabled: e.target.checked })}
            />
            Sauvegardes automatiques activées (export de configuration + sauvegarde binaire)
          </label>

          <div className="text-[11px] text-muted-2 mt-3 leading-relaxed">
            L'utilisateur RouterOS doit appartenir au groupe <code className="font-mono">full</code> (lecture, écriture,
            redémarrage, accès aux secrets pour l'export). La connexion part du serveur RMM : le service SSH du routeur
            doit lui être ouvert.
          </div>

          {/* ─── Test de connexion ─── */}
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={runTest}
              disabled={testing || !accessOk}
              className="bg-rule border border-rule-3 rounded-lg px-3.5 py-2 text-fg-2 cursor-pointer text-xs font-semibold hover:bg-rule-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {testing && <Loader2 size={13} className="animate-spin" />}
              {testing ? 'Test en cours…' : 'Tester la connexion'}
            </button>
            {!editing && !test && (
              <span className="text-[11px] text-muted-2">Recommandé : vérifie l'accès et fixe l'empreinte SSH.</span>
            )}
          </div>

          {test && test.success && (
            <div className="mt-3 bg-ok/10 border border-ok/30 rounded-xl px-3.5 py-3 text-[12px] text-fg-2">
              <div className="flex items-center gap-1.5 font-semibold text-ok mb-1.5">
                <CheckCircle2 size={14} strokeWidth={2} /> Connexion réussie
              </div>
              <div className="grid grid-cols-[110px_1fr] gap-y-0.5">
                <span className="text-muted-2">Identité</span>
                <span className="font-mono">{test.identity || '—'}</span>
                <span className="text-muted-2">Modèle</span>
                <span className="font-mono">{[test.model, test.boardName].filter(Boolean).join(' · ') || '—'}</span>
                <span className="text-muted-2">RouterOS</span>
                <span className="font-mono">{test.routerOsVersion || '—'}</span>
                <span className="text-muted-2">Empreinte SSH</span>
                <span className="font-mono break-all">
                  {test.hostKeyAlgorithm ? test.hostKeyAlgorithm + ' ' : ''}
                  {test.hostKeyFingerprint || '—'}
                </span>
              </div>
              <div className="text-[11px] text-muted mt-2">
                Cette empreinte sera mémorisée ; une clé différente plus tard bloquera l'accès.
              </div>
            </div>
          )}

          {test && !test.success && (
            <div className="mt-3 bg-crit/10 border border-crit/30 rounded-xl px-3.5 py-2.5 text-[12px] text-fg-2 flex gap-2">
              <AlertTriangle size={14} strokeWidth={1.75} className="text-crit flex-shrink-0 mt-0.5" />
              <span>{testFailureMessage(test)}</span>
            </div>
          )}

          {error && !(test && !test.success) && (
            <div className="mt-3 bg-crit/10 border border-crit/30 rounded-xl px-3.5 py-2.5 text-[12px] text-fg-2 flex gap-2">
              <AlertTriangle size={14} strokeWidth={1.75} className="text-crit flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-2.5 mt-5">
            <button
              onClick={() => onClose(false)}
              className="bg-rule border border-rule-3 rounded-lg px-4 py-2 text-muted cursor-pointer text-[13px] font-semibold"
            >
              Annuler
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit}
              className="bg-blue-500 border-none rounded-lg px-4 py-2 text-white cursor-pointer text-[13px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600"
            >
              {saving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
